import {
  Markup,
  Tax,
  MarkupAmountType,
  TaxAmountType,
} from 'lane-shared/domains/billingPayments/types';
import { AdminFee, AdminFeeAmountType } from 'application-types';
import { getCurrencyPrecision, type SupportedPaymentCurrency } from 'domains-billing-currency';
import { formatCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';

import { PriceComponents } from '../types';
import BigNumber from 'bignumber.js';

export type PricingParams = {
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  priceComponents: PriceComponents;
  inclusivePricing?: boolean;
  isAdminFeeFeatureEnabled?: boolean;
};

export type Pricing = {
  rate: number; // Base amount
  grossMarkup: number; // Markup amount in unit currency
  grossTax: number; // Tax amount in unit currency
  grossAdminFee: number; // Admin fee amount in unit currency
  listPrice: number; // Sum of rate, markup, and admin fee amounts in unit currency
  netPrice: number; // Price after applying tax to listPrice in unit currency
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string; // Formatter function that converts the price to a string
};

function isPercentageBased(optRate: Markup | Tax | AdminFee): boolean {
  if ('markupAmountType' in optRate) {
    return (
      optRate.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
    );
  }

  if ('taxAmountType' in optRate) {
    return optRate.taxAmountType === TaxAmountType.TaxAmountTypePercentage;
  }

  return (
    optRate.adminFeeAmountType === AdminFeeAmountType.AdminFeeAmountTypePercentage
  );
}

export function calculateExclusiveOptionalRate(
  rate: BigNumber,
  optRate: Markup | Tax | AdminFee
) {
  const { value } = optRate;
  const isPercentage = isPercentageBased(optRate);

  const calculatedValueForPercentage = Number(
    rate.multipliedBy(value).dividedBy(100).toFixed(2)
  );

  return isPercentage ? calculatedValueForPercentage || 0 : value || 0;
}

export function calculateInclusiveOptionalRate(
  price: BigNumber,
  rate: BigNumber
) {
  return price.minus(rate).toNumber() || 0;
}

export function calculateBaseRateFromInclusiveAmount(
  price: BigNumber,
  optRate: Markup | Tax | AdminFee
) {
  const { value } = optRate;
  const isPercentage = isPercentageBased(optRate);

  return isPercentage
    ? price.dividedBy(1 + value / 100).toNumber() || 0
    : price.minus(value).toNumber() || 0;
}

export function getPricing({
  currency,
  locale,
  priceComponents,
  inclusivePricing = false,
  isAdminFeeFeatureEnabled = false,
}: PricingParams): Pricing {
  const { sellingPrice, amount } = priceComponents;

  const precision = getCurrencyPrecision(currency);

  const formatPrice = (
    amount: number,
    currencyDisplay: 'narrowSymbol' | 'none' = 'none'
  ) =>
    formatCurrency(currency, {
      locale,
      currencyDisplay,
      useGrouping: false,
    })(amount);

  let rate = 0;
  let grossMarkup = 0;
  let grossTax = 0;
  let grossAdminFee = 0;
  let listPrice = 0;

  if (
    sellingPrice?.listPrice !== undefined &&
    sellingPrice.grossTax !== undefined &&
    sellingPrice.netPrice !== undefined &&
    sellingPrice.grossMarkup !== undefined
  ) {
    listPrice = Number(formatPrice(sellingPrice.listPrice));
    grossTax = Number(formatPrice(sellingPrice.grossTax));
    rate = Number(formatPrice(amount));
    grossMarkup = Number(formatPrice(sellingPrice.grossMarkup));
    grossAdminFee = Number(formatPrice(sellingPrice.grossAdminFee ?? 0));
  } else if (sellingPrice?.listPrice !== undefined) {
    ({ listPrice, rate, grossMarkup, grossTax, grossAdminFee } = calculateListPriceOverridePricing(
      priceComponents,
      formatPrice,
      precision,
      isAdminFeeFeatureEnabled
    ));
  } else if (inclusivePricing) {
    ({ listPrice, grossTax, rate, grossMarkup, grossAdminFee } = (
      isAdminFeeFeatureEnabled
        ? calculateInclusivePricingWithRateFees
        : calculateInclusivePricing
    )(priceComponents, formatPrice, precision));
  } else {
    ({ listPrice, grossTax, rate, grossMarkup, grossAdminFee } = (
      isAdminFeeFeatureEnabled
        ? calculateExclusivePricingWithRateFees
        : calculateExclusivePricing
    )(priceComponents, formatPrice, precision));
  }

  return {
    rate,
    grossMarkup,
    grossTax,
    grossAdminFee,
    listPrice,
    netPrice: new BigNumber(listPrice).plus(grossTax).toNumber(),
    formatPrice,
  };
}

function calculateListPriceOverridePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number,
  isAdminFeeFeatureEnabled: boolean
) {
  const { sellingPrice, amount } = priceComponents;

  const listPrice = Number(formatPrice(sellingPrice!.listPrice || 0));
  const rate = Number(formatPrice(amount));
  const grossMarkup = Number(formatPrice(sellingPrice!.grossMarkup || 0));
  const grossAdminFee = Number(formatPrice(sellingPrice!.grossAdminFee || 0));

  const taxableAmount = isAdminFeeFeatureEnabled
    ? new BigNumber(listPrice).minus(grossAdminFee)
    : new BigNumber(listPrice);
  const grossTax = new BigNumber(
    priceComponents.tax
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(taxableAmount, priceComponents.tax)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    rate,
    grossMarkup,
    grossAdminFee,
    grossTax,
  };
}

function calculateInclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, netPrice } = priceComponents;

  const listPrice = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateBaseRateFromInclusiveAmount(
            new BigNumber(netPrice ?? 0),
            tax
          )
        )
      )
      : (netPrice ?? 0)
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossTax = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateInclusiveOptionalRate(
            new BigNumber(netPrice ?? 0),
            new BigNumber(listPrice)
          )
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const rate = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateBaseRateFromInclusiveAmount(new BigNumber(listPrice), markup)
        )
      )
      : listPrice
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateInclusiveOptionalRate(
            new BigNumber(listPrice),
            new BigNumber(rate)
          )
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
  };
}

// Types for inclusive pricing helpers
type FormatPriceFn = (amount: number, currencyDisplay?: 'narrowSymbol' | 'none') => string;

type InclusiveRateConfig = {
  markupRate: number;
  taxRate: number;
  adminFeeRate: number;
  // adminFeeTaxRate: number; // TODO: Add when implemented
  hasMarkup: boolean;
  hasAdminFee: boolean;
  hasTax: boolean;
  markupIsPercentage: boolean;
  adminFeeIsPercentage: boolean;
  taxIsPercentage: boolean;
  fixedMarkup: number;
  fixedAdminFee: number;
  fixedTax: number;
};

type InclusivePricingResult = {
  rate: number;
  grossAdminFee: number;
  grossTax: number;
  grossMarkup: number;
};

function extractInclusiveRateConfig(priceComponents: PriceComponents): InclusiveRateConfig {
  const { markup, adminFee, tax } = priceComponents;

  return {
    markupRate: markup && isPercentageBased(markup) ? markup.value / 100 : 0,
    taxRate: tax && isPercentageBased(tax) ? tax.value / 100 : 0,
    adminFeeRate: adminFee && isPercentageBased(adminFee) ? adminFee.value / 100 : 0,
    hasMarkup: !!(markup && markup.value > 0),
    hasAdminFee: !!(adminFee && adminFee.value > 0),
    hasTax: !!(tax && tax.value > 0),
    markupIsPercentage: !!(markup && isPercentageBased(markup)),
    adminFeeIsPercentage: !!(adminFee && isPercentageBased(adminFee)),
    taxIsPercentage: !!(tax && isPercentageBased(tax)),
    fixedMarkup: markup && !isPercentageBased(markup) ? (markup.value || 0) : 0,
    fixedAdminFee: adminFee && !isPercentageBased(adminFee) ? (adminFee.value || 0) : 0,
    fixedTax: tax && !isPercentageBased(tax) ? (tax.value || 0) : 0,
  };
}

function calculateInclusiveDivisor(config: InclusiveRateConfig, withMarkup: boolean): number {
  const { markupRate, taxRate, adminFeeRate } = config;

  if (withMarkup) {
    // TODO: + (adminFeeRate × adminFeeTaxRate) when Admin Fee Tax is added
    return (1 + markupRate) * (1 + taxRate) + adminFeeRate;
  }
  // TODO: × (1 + adminFeeTaxRate) when Admin Fee Tax is added
  return adminFeeRate > 0 ? (1 + taxRate) + adminFeeRate : (1 + taxRate);
}

function calculateInclusiveWithMarkupPlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  formatPrice: FormatPriceFn,
  precision: number
): InclusivePricingResult {
  const { markupRate, taxRate, adminFeeRate, hasTax, taxIsPercentage, fixedTax, hasAdminFee, adminFeeIsPercentage, fixedAdminFee } = config;

  const divisor = calculateInclusiveDivisor(config, true);

  // For fixed admin fee and fixed tax, subtract them from the total before calculating
  // Only percentage admin fee and percentage tax are included in the divisor
  let amountForCalculation = netPriceValue;
  if (!adminFeeIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedAdminFee);
  }
  if (!taxIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedTax);
  }

  // Step 1: Base (anchor) = (Total - fixedAdminFee - fixedTax) / divisor
  const rate = new BigNumber(
    Number(formatPrice(amountForCalculation.dividedBy(divisor).toNumber()))
  ).decimalPlaces(precision).toNumber();

  // Step 2: Admin Fee = Base × adminFeeRate (or fixed)
  const grossAdminFee = new BigNumber(
    hasAdminFee && adminFeeIsPercentage
      ? Number(formatPrice(new BigNumber(rate).multipliedBy(adminFeeRate).toNumber()))
      : Number(formatPrice(fixedAdminFee))
  ).decimalPlaces(precision).toNumber();

  // TODO: Step 3: Calculate Admin Fee Tax = Admin Fee × adminFeeTaxRate

  // Step 4: Sales Tax = (Base + Markup_estimated) × taxRate (percentage) or fixed amount
  const markupEstimated = new BigNumber(rate).multipliedBy(markupRate);
  let grossTaxValue = 0;
  if (hasTax && taxIsPercentage) {
    grossTaxValue = Number(formatPrice(new BigNumber(rate).plus(markupEstimated).multipliedBy(taxRate).toNumber()));
  } else if (hasTax) {
    grossTaxValue = Number(formatPrice(fixedTax));
  }
  const grossTax = new BigNumber(grossTaxValue).decimalPlaces(precision).toNumber();

  // Step 5: Markup = Total - Base - AdminFee - Tax (plug absorbs rounding)
  // TODO: subtract grossAdminFeeTax when Admin Fee Tax is added
  const grossMarkup = new BigNumber(
    Number(formatPrice(netPriceValue.minus(rate).minus(grossAdminFee).minus(grossTax).toNumber()))
  ).decimalPlaces(precision).toNumber();

  return { rate, grossAdminFee, grossTax, grossMarkup };
}

function calculateInclusiveWithAdminFeePlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  formatPrice: FormatPriceFn,
  precision: number
): InclusivePricingResult {
  const { taxRate, hasTax, taxIsPercentage, fixedTax, hasMarkup, hasAdminFee, fixedMarkup, fixedAdminFee } = config;

  const divisor = calculateInclusiveDivisor(config, false);

  // Step 1: Base = (Total - fixedMarkup - fixedAdminFee - fixedTax - taxOnFixedMarkup) / divisor
  // Only percentage tax is included in the divisor; fixed amounts must be subtracted
  // Tax on fixed markup must also be subtracted since it's not captured in the divisor
  const taxOnFixedMarkup = taxIsPercentage ? new BigNumber(fixedMarkup).multipliedBy(taxRate) : new BigNumber(0);
  const baseBeforeFixedFees = netPriceValue
    .minus(fixedMarkup)
    .minus(fixedAdminFee)
    .minus(fixedTax)
    .minus(taxOnFixedMarkup);
  const rate = new BigNumber(
    Number(formatPrice(baseBeforeFixedFees.dividedBy(divisor).toNumber()))
  ).decimalPlaces(precision).toNumber();

  // Step 2: Sales Tax = (Base + fixedMarkup) × taxRate (percentage) or fixed amount
  // Tax applies to both rate and markup, consistent with other pricing paths
  let grossTaxValue = 0;
  if (hasTax && taxIsPercentage) {
    grossTaxValue = Number(formatPrice(new BigNumber(rate).plus(fixedMarkup).multipliedBy(taxRate).toNumber()));
  } else if (hasTax) {
    grossTaxValue = Number(formatPrice(fixedTax));
  }
  const grossTax = new BigNumber(grossTaxValue).decimalPlaces(precision).toNumber();

  // Step 3: Markup (fixed or 0)
  const grossMarkup = new BigNumber(
    hasMarkup ? Number(formatPrice(fixedMarkup)) : 0
  ).decimalPlaces(precision).toNumber();

  // Step 4: Admin Fee = Total - Base - Tax - Markup (plug absorbs rounding)
  // TODO: When Admin Fee Tax is added: grossAdminFee = combinedAdmin / (1 + adminFeeTaxRate)
  const combinedAdmin = netPriceValue.minus(rate).minus(grossTax).minus(grossMarkup);
  const grossAdminFee = new BigNumber(
    hasAdminFee ? Number(formatPrice(combinedAdmin.toNumber())) : 0
  ).decimalPlaces(precision).toNumber();

  return { rate, grossAdminFee, grossTax, grossMarkup };
}

function calculateInclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  formatPrice: FormatPriceFn,
  precision: number
) {
  const netPriceValue = new BigNumber(priceComponents.netPrice ?? 0);

  // Early return for zero or invalid netPrice to avoid NaN
  if (netPriceValue.isZero() || netPriceValue.isNaN()) {
    return {
      listPrice: 0,
      grossTax: 0,
      rate: 0,
      grossMarkup: 0,
      grossAdminFee: 0,
    };
  }

  const config = extractInclusiveRateConfig(priceComponents);

  const { rate, grossAdminFee, grossTax, grossMarkup } =
    config.hasMarkup && config.markupIsPercentage
      ? calculateInclusiveWithMarkupPlug(netPriceValue, config, formatPrice, precision)
      : calculateInclusiveWithAdminFeePlug(netPriceValue, config, formatPrice, precision);

  const listPrice = new BigNumber(rate)
    .plus(grossMarkup)
    .plus(grossAdminFee)
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
  };
}

function calculateExclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, amount } = priceComponents;

  const rate = new BigNumber(amount || 0)
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(new BigNumber(amount), markup)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const listPrice = new BigNumber(amount)
    .plus(grossMarkup)
    .decimalPlaces(precision)
    .toNumber() || 0;

  const grossTax = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(new BigNumber(listPrice), tax)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
  };
}

function calculateExclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, adminFee, tax, amount } = priceComponents;

  const rate = new BigNumber(amount || 0)
    .decimalPlaces(precision)
    .toNumber();

  let grossMarkupValue = 0;
  if (markup) {
    const rawMarkup = calculateExclusiveOptionalRate(new BigNumber(amount), markup);
    grossMarkupValue = Number(formatPrice(rawMarkup));
  }
  const grossMarkup = new BigNumber(grossMarkupValue)
    .decimalPlaces(precision)
    .toNumber();

  let grossAdminFeeValue = 0;
  if (adminFee) {
    const rawAdminFee = calculateExclusiveOptionalRate(new BigNumber(amount), adminFee);
    grossAdminFeeValue = Number(formatPrice(rawAdminFee));
  }
  const grossAdminFee = new BigNumber(grossAdminFeeValue)
    .decimalPlaces(precision)
    .toNumber();

  // TODO: Calculate grossAdminFeeTax when Admin Fee Tax is implemented
  const listPrice = new BigNumber(amount)
    .plus(grossMarkup)
    .plus(grossAdminFee)
    .decimalPlaces(precision)
    .toNumber() || 0;

  const taxableAmount = new BigNumber(amount).plus(grossMarkup);
  let grossTaxValue = 0;
  if (tax) {
    const rawTax = calculateExclusiveOptionalRate(taxableAmount, tax);
    grossTaxValue = Number(formatPrice(rawTax));
  }
  const grossTax = new BigNumber(grossTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
    // TODO: Add grossAdminFeeTax when Admin Fee Tax is implemented
  };
}

